import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { FILTER_FIELDS } from "./filterFields";

type WIPStatus =
  | "SIN_FECHAS"
  | "PENDIENTE_INICIO"
  | "FINALIZADA"
  | "INICIO_PENDIENTE"
  | "ATRASADO"
  | "EN_RITMO"
  | "ADELANTADO";

const ALL_STATUSES: WIPStatus[] = [
  "SIN_FECHAS",
  "PENDIENTE_INICIO",
  "FINALIZADA",
  "INICIO_PENDIENTE",
  "ATRASADO",
  "EN_RITMO",
  "ADELANTADO",
];

const STATUS_LABEL: Record<WIPStatus, string> = {
  SIN_FECHAS: "Sin fechas",
  PENDIENTE_INICIO: "Pendiente de inicio",
  FINALIZADA: "Finalizada",
  INICIO_PENDIENTE: "Inicio pendiente",
  ATRASADO: "Atrasada",
  EN_RITMO: "En ritmo",
  ADELANTADO: "Adelantada",
};

/** A selectable option for a field filter: raw value + display label. */
export interface FacetOption {
  value: string;
  label: string;
  /** Heading the option is listed under (e.g. the client of a briefing). */
  group?: string;
}

interface FiltersProps {
  statusFilter: string[];
  textFilter: string;
  fieldFilters: Record<string, string[]>;
  /** Available options per field, keyed by field id. */
  facets: Record<string, FacetOption[]>;
  onStatusChange: (statuses: string[]) => void;
  onTextChange: (text: string) => void;
  onFieldFilterChange: (field: string, values: string[]) => void;
  onClear: () => void;
}

/** Searchable multi-select: type to find an option, tick several, grouped
 *  when the options carry a group. */
const MultiSelectFilter: React.FC<{
  id: string;
  label: string;
  minWidth: number;
  selected: string[];
  options: FacetOption[];
  optionLabel: (value: string) => string;
  onChange: (values: string[]) => void;
}> = ({ id, label, minWidth, selected, options, optionLabel, onChange }) => {
  // Selected values stay selectable even if the current options no longer
  // list them (e.g. a briefing whose client was just unticked).
  const byValue = new Map(options.map((o) => [o.value, o]));
  const value = selected.map(
    (v) => byValue.get(v) ?? { value: v, label: optionLabel(v) }
  );
  const grouped = options.some((o) => o.group);

  return (
    <Autocomplete<FacetOption, true, false, false>
      id={id}
      multiple
      size="small"
      disableCloseOnSelect
      limitTags={2}
      options={options}
      value={value}
      groupBy={grouped ? (o) => o.group ?? "" : undefined}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      onChange={(_e, next) => onChange(next.map((o) => o.value))}
      noOptionsText="Sin coincidencias"
      renderOption={(props, option, { selected: isSelected }) => (
        <li {...props} key={option.value}>
          <Checkbox checked={isSelected} size="small" sx={{ mr: 1, p: 0 }} />
          <ListItemText primary={option.label} />
        </li>
      )}
      renderTags={(tags, getTagProps) =>
        tags.map((o, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} label={o.label} size="small" {...tagProps} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Escribe para buscar"
        />
      )}
      sx={{ minWidth, maxWidth: 360 }}
    />
  );
};

const Filters: React.FC<FiltersProps> = ({
  statusFilter,
  textFilter,
  fieldFilters,
  facets,
  onStatusChange,
  onTextChange,
  onFieldFilterChange,
  onClear,
}) => {
  const hasActiveFieldFilters = Object.values(fieldFilters).some(
    (v) => v.length > 0
  );
  const hasActiveFilters =
    statusFilter.length > 0 ||
    textFilter.trim() !== "" ||
    hasActiveFieldFilters;

  const handleStatusChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onStatusChange(typeof value === "string" ? value.split(",") : value);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        alignItems: "center",
      }}
    >
      {/* Per-field multi-selects (cliente, briefing, plataforma, …) */}
      {FILTER_FIELDS.map((f) => (
        <MultiSelectFilter
          key={f.key}
          id={`buy-media-filter-${f.key}`}
          label={f.label}
          minWidth={f.minWidth}
          selected={fieldFilters[f.key] ?? []}
          options={facets[f.key] ?? []}
          optionLabel={f.formatOption}
          onChange={(values) => onFieldFilterChange(f.key, values)}
        />
      ))}

      {/* Status multi-select */}
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel id="buy-media-status-label">Estado</InputLabel>
        <Select
          labelId="buy-media-status-label"
          multiple
          value={statusFilter}
          onChange={handleStatusChange}
          input={<OutlinedInput label="Estado" />}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {(selected as string[]).map((value) => (
                <Chip
                  key={value}
                  label={STATUS_LABEL[value as WIPStatus] ?? value}
                  size="small"
                />
              ))}
            </Box>
          )}
        >
          {ALL_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              <Checkbox checked={statusFilter.includes(s)} size="small" />
              <ListItemText primary={STATUS_LABEL[s]} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Text search */}
      <TextField
        size="small"
        label="Buscar cliente / briefing / subcampaña"
        value={textFilter}
        onChange={(e) => onTextChange(e.target.value)}
        sx={{ minWidth: 280 }}
        slotProps={{ input: { sx: { fontSize: 13 } } }}
      />

      {/* Clear button — only shown when filters are active */}
      {hasActiveFilters && (
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<ClearIcon />}
          onClick={onClear}
          sx={{ whiteSpace: "nowrap" }}
        >
          Limpiar filtros
        </Button>
      )}
    </Box>
  );
};

export default Filters;
